<script setup>
import { ref, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';

defineProps({
  nav: {
    type: Array,
    default: () => [],
  },
  additionalLinks: {
    type: Array,
    default: () => [],
  },
});

const isTabletOrDesktop = useMediaQuery('(min-width: 768px)');
const isDesktop = useMediaQuery('(min-width: 1024px)');

const openItems = ref(new Set());

const toggleItem = (sectionTitle) => {
  if (!isTabletOrDesktop.value) {
    if (openItems.value.has(sectionTitle)) {
      openItems.value.delete(sectionTitle);
    } else {
      openItems.value.add(sectionTitle);
    }
    openItems.value = new Set(openItems.value);
  }
};

const isItemOpen = (sectionTitle) => {
  if (isTabletOrDesktop.value) {
    return true;
  }
  return openItems.value.has(sectionTitle);
};

watch(isTabletOrDesktop, (newValue) => {
  if (newValue) {
    openItems.value.clear();
  }
});
</script>

<template>
  <div class="footer__nav">
    <ul
      v-if="
        (nav && nav.length > 0) ||
        (additionalLinks && additionalLinks.length > 0)
      "
      class="m-0 p-0 footer__nav-items"
    >
      <template v-if="nav && nav.length > 0">
        <li
          v-for="section in nav"
          :key="section.title"
          class="m-0 footer__nav-item"
          :class="{ 'footer__nav-item--open': isItemOpen(section.title) }"
        >
          <div
            class="footer__nav-item-parent"
            :class="{
              'footer__nav-item-parent--with-children':
                section.children && section.children.length > 0,
            }"
          >
            <CTagDetect
              v-if="section.url"
              :to="section.url"
              class="footer__nav-item-link link-normal"
              >{{ section.title }}</CTagDetect
            >
            <span
              v-else
              class="footer__nav-item-link link-normal"
              v-html="section.title"
            ></span>
            <button
              v-if="section.children && section.children.length > 0"
              type="button"
              class="footer__nav-item-button"
              :aria-expanded="isItemOpen(section.title)"
              :aria-label="`Toggle ${section.title} submenu`"
              @click="toggleItem(section.title)"
            >
              <CIcon
                name="direction-small-white"
                width="16"
                height="16"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          </div>
          <ul
            v-if="section.children && section.children.length > 0"
            class="m-0 p-0 footer__subnav"
          >
            <li
              v-for="item in section.children"
              :key="item"
              class="m-0 footer__subnav-item"
            >
              <CTagDetect
                v-if="item.url && item.url.length > 1"
                :to="item.url"
                class="footer__subnav-item-link link-normal"
                >{{ item.title }}</CTagDetect
              >
              <span v-else class="footer__subnav-item-link link-normal">
                {{ item.title }}
              </span>
            </li>
          </ul>
        </li>
      </template>
      <template
        v-if="additionalLinks && additionalLinks.length > 0 && !isDesktop"
      >
        <li
          v-for="section in additionalLinks"
          :key="section.title"
          class="footer__nav-item"
          :class="{ 'footer__nav-item--open': isItemOpen(section.title) }"
        >
          <div
            class="footer__nav-item-parent"
            :class="{
              'footer__nav-item-parent--with-children':
                section.children && section.children.length > 0,
            }"
          >
            <CTagDetect
              v-if="section.url && section.url.length > 1"
              :to="section.url"
              class="footer__nav-item-link link-normal"
              >{{ section.title }}</CTagDetect
            >
            <span v-else class="footer__nav-item-link link-normal">{{
              section.title
            }}</span>
            <button
              v-if="section.children && section.children.length > 0"
              type="button"
              class="footer__nav-item-button"
              :aria-expanded="isItemOpen(section.title)"
              :aria-label="`Toggle ${section.title} submenu`"
              @click="toggleItem(section.title)"
            >
              <CIcon
                name="direction-small-white"
                width="16"
                height="16"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          </div>
          <ul
            v-if="section.children && section.children.length > 0"
            class="m-0 p-0 footer__subnav"
          >
            <li
              v-for="item in section.children"
              :key="item"
              class="m-0 footer__subnav-item"
            >
              <CTagDetect
                v-if="item.url && item.url.length > 1"
                :to="item.url"
                class="footer__subnav-item-link link-normal"
                >{{ item.title }}</CTagDetect
              >
              <span v-else class="footer__subnav-item-link link-normal">
                {{ item.title }}
              </span>
            </li>
          </ul>
        </li>
      </template>
    </ul>
    <ul
      v-if="additionalLinks && additionalLinks.length > 0 && isDesktop"
      class="m-0 p-0 footer__nav-items footer__nav-items--additional"
    >
      <li
        v-for="section in additionalLinks"
        :key="section.title"
        class="m-0 footer__nav-item"
        :class="{ 'footer__nav-item--open': isItemOpen(section.title) }"
      >
        <div
          class="footer__nav-item-parent"
          :class="{
            'footer__nav-item-parent--with-children':
              section.children && section.children.length > 0,
          }"
        >
          <CTagDetect
            v-if="section.url && section.url.length > 1"
            :to="section.url"
            class="footer__nav-item-link link-normal"
            >{{ section.title }}</CTagDetect
          >
          <span v-else class="footer__nav-item-link link-normal">{{
            section.title
          }}</span>
          <button
            v-if="section.children && section.children.length > 0"
            type="button"
            class="footer__nav-item-button"
            :aria-expanded="isItemOpen(section.title)"
            :aria-label="`Toggle ${section.title} submenu`"
            @click="toggleItem(section.title)"
          >
            <CIcon
              name="direction-small-white"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            />
          </button>
        </div>
        <ul
          v-if="section.children && section.children.length > 0"
          class="m-0 p-0 footer__subnav"
        >
          <li
            v-for="item in section.children"
            :key="item"
            class="m-0 footer__subnav-item"
          >
            <CTagDetect
              v-if="item.url && item.url.length > 1"
              :to="item.url"
              class="footer__subnav-item-link link-normal"
              >{{ item.title }}</CTagDetect
            >
            <span v-else class="footer__subnav-item-link link-normal">{{
              item.title
            }}</span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.footer {
  $parent: &;
  &__nav {
    @include media-breakpoint-up(md) {
      display: flex;
      flex-direction: column;
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-items {
      list-style: none;
      @include media-breakpoint-up(sm) {
        column-count: 3;
        column-gap: 16px;
      }
      @include media-breakpoint-up(md) {
        &--additional {
          display: flex;
          flex-wrap: wrap;
          flex-direction: row;
          gap: 48px;
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        &--additional {
          @include responsive-spacing(gap, 48);
        }
      }
    }
    &-item {
      display: flex;
      flex-direction: column;
      break-inside: avoid;
      margin-bottom: 12px;
      page-break-inside: avoid;
      -webkit-column-break-inside: avoid;
      @include media-breakpoint-up(sm) {
        gap: 16px;
        margin-bottom: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        @include responsive-spacing(margin-bottom, 48);
      }
      &-parent {
        &--with-children {
          align-items: center;
          display: grid;
          gap: 8px;
          grid-template-columns: min-content 16px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 8);
            grid-template-columns: min-content px-to-vw(16);
          }
        }
      }
      &-link {
        padding: 7px 0 8px;
        display: inline-flex;
        white-space: nowrap;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 7);
          @include responsive-spacing(padding-bottom, 8);
        }
      }
      &-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        padding: 0;
        border: none;
        background: none;
        transform: rotate(90deg);
        transition: transform 0.3s ease;
        cursor: pointer;
        @include media-breakpoint-up(sm) {
          transform: rotate(0deg);
        }
      }
      &--open {
        #{$parent} {
          &__subnav {
            max-height: 500px;
            opacity: 1;
            visibility: visible;
            @include media-breakpoint-up(sm) {
              max-height: unset;
            }
          }
          &__nav-item-button {
            transform: rotate(-90deg);
            @include media-breakpoint-up(sm) {
              transform: rotate(0deg);
            }
          }
        }
      }
    }
  }
  &__subnav {
    break-inside: avoid;
    page-break-inside: avoid;
    -webkit-column-break-inside: avoid;
    overflow: hidden;
    transition: all 0.3s ease;
    max-height: 0;
    opacity: 0;
    visibility: hidden;
    padding-top: 0;
    list-style: none;
    @include media-breakpoint-up(sm) {
      max-height: none;
      opacity: 1;
      visibility: visible;
      transition: none;
    }
    &-item {
      &-link {
        padding: 7px 0;
        display: inline-flex;
        @include media-breakpoint-up(sm) {
          padding: 9px 0;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 9);
          @include responsive-spacing(padding-bottom, 9);
        }
      }
    }
  }
}
</style>
